import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';

export default function GEOPage() {
  const geoSteps = [
    {
      number: '01',
      title: 'Assess Prompts',
      description: 'Test how AI assistants respond to queries in your market. Identify gaps and opportunities.',
      icon: 'ri-search-line'
    },
    {
      number: '02',
      title: 'Entity Clarity',
      description: 'Ensure AI systems understand exactly who you are, what you do, and where you serve.',
      icon: 'ri-building-line'
    },
    {
      number: '03',
      title: 'Structured Content',
      description: 'Create answer-first content that AI can easily parse and cite in responses.',
      icon: 'ri-file-text-line'
    },
    {
      number: '04',
      title: 'FAQ Architecture',
      description: 'Build comprehensive FAQ sections that match common AI queries in your industry.',
      icon: 'ri-question-answer-line'
    },
    {
      number: '05',
      title: 'Schema Markup',
      description: 'Implement structured data that helps AI understand your services, reviews, and offerings.',
      icon: 'ri-code-s-slash-line'
    },
    {
      number: '06',
      title: 'Citations & Authority',
      description: 'Build authoritative signals that AI systems trust when forming recommendations.',
      icon: 'ri-medal-line'
    },
    {
      number: '07',
      title: 'Comparison Content',
      description: 'Create content that positions you favorably when AI compares options.',
      icon: 'ri-scales-line'
    },
    {
      number: '08',
      title: 'Regular Updates',
      description: 'Keep content fresh so AI systems see you as current and relevant.',
      icon: 'ri-refresh-line'
    },
    {
      number: '09',
      title: 'Monitoring',
      description: 'Track how AI assistants mention your brand across multiple platforms.',
      icon: 'ri-eye-line'
    },
    {
      number: '10',
      title: 'Reporting',
      description: 'Receive clear reports showing AI visibility improvements and competitive positioning.',
      icon: 'ri-bar-chart-line'
    }
  ];

  const deliverables = [
    'AI Visibility Baseline Report',
    'Competitive AI Mention Analysis',
    'Entity Optimization Implementation',
    'Answer-First Content Strategy',
    'FAQ Section Development',
    'Schema Markup Installation',
    'Citation Building Campaign',
    'Monthly AI Visibility Tracking',
    'Quarterly Strategy Reviews',
    'Ongoing Content Optimization'
  ];

  const aiPlatforms = [
    { name: 'ChatGPT', icon: 'ri-chat-3-line', color: 'bg-navy-800' },
    { name: 'Perplexity', icon: 'ri-search-eye-line', color: 'bg-navy-800' },
    { name: 'Google SGE', icon: 'ri-google-line', color: 'bg-navy-800' },
    { name: 'Bing Copilot', icon: 'ri-microsoft-line', color: 'bg-navy-800' },
    { name: 'Claude', icon: 'ri-robot-2-line', color: 'bg-navy-800' },
    { name: 'Gemini', icon: 'ri-gemini-line', color: 'bg-navy-800' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero */}
      <PageHero
        eyebrow="Generative Engine Optimization"
        title="GEO: Get Your Brand Recommended by AI"
        subtitle="Optimize your digital presence so AI assistants like ChatGPT, Perplexity, Google SGE, and Bing Copilot mention and recommend your Florida business when customers ask for help."
      >
        <Link to="/audit" className="fdme-cta inline-flex items-center gap-2 px-7 py-3.5 text-base">
          Get AI Visibility Assessment <i className="ri-arrow-right-up-line"></i>
        </Link>
      </PageHero>

      {/* AI Platforms We Track */}
      <section className="px-6 lg:px-12 py-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-navy-900 mb-4">
              We Track Your Visibility Across All Major AI Platforms
            </h2>
            <p className="text-lg text-navy-600">
              Monitor how AI assistants mention your brand across the entire ecosystem
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {aiPlatforms.map((platform, index) => (
              <div key={index} className="bg-navy-50 rounded-xl p-6 text-center hover:shadow-lg transition-all">
                <div className={`w-14 h-14 ${platform.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                  <i className={`${platform.icon} text-2xl text-orange-400`}></i>
                </div>
                <h3 className="text-sm font-bold text-navy-900">{platform.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GEO vs SEO */}
      <section className="px-6 lg:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              GEO vs SEO: What's the Difference?
            </h2>
            <p className="text-xl text-navy-600 max-w-3xl mx-auto">
              Traditional SEO gets you ranked. GEO gets you recommended.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* SEO */}
            <div className="bg-navy-50 rounded-2xl p-8 border-2 border-navy-100">
              <div className="w-16 h-16 bg-navy-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-search-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-4">
                Traditional SEO
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-lg text-navy-600 mt-0.5"></i>
                  <span className="text-navy-700">Optimizes for search engine rankings</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-lg text-navy-600 mt-0.5"></i>
                  <span className="text-navy-700">Goal: Appear on page one</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-lg text-navy-600 mt-0.5"></i>
                  <span className="text-navy-700">Users click through to your site</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-lg text-navy-600 mt-0.5"></i>
                  <span className="text-navy-700">Focuses on keywords and backlinks</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-lg text-navy-600 mt-0.5"></i>
                  <span className="text-navy-700">Measures traffic and rankings</span>
                </li>
              </ul>
            </div>

            {/* GEO */}
            <div className="bg-teal-50 rounded-2xl p-8 border-2 border-teal-500">
              <div className="w-16 h-16 bg-teal-500 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-robot-2-line text-3xl text-navy-900"></i>
              </div>
              <h3 className="text-2xl font-bold font-display text-navy-900 mb-4">
                GEO (Generative Engine Optimization)
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-lg text-teal-600 mt-0.5"></i>
                  <span className="text-navy-700"><strong>Optimizes for AI recommendations</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-lg text-teal-600 mt-0.5"></i>
                  <span className="text-navy-700"><strong>Goal: Be mentioned in the answer</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-lg text-teal-600 mt-0.5"></i>
                  <span className="text-navy-700"><strong>AI recommends you directly</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-lg text-teal-600 mt-0.5"></i>
                  <span className="text-navy-700"><strong>Focuses on entity clarity and authority</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-lg text-teal-600 mt-0.5"></i>
                  <span className="text-navy-700"><strong>Measures AI mentions and sentiment</strong></span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl p-8 text-center text-white">
            <p className="text-xl font-semibold">
              The future isn't about ranking #1 on Google. It's about being the brand AI recommends when customers ask for help.
            </p>
          </div>
        </div>
      </section>

      {/* 10-Step Process */}
      <section className="px-6 lg:px-12 py-20 bg-navy-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              Our 10-Step GEO Process
            </h2>
            <p className="text-xl text-navy-600">
              A systematic approach to AI visibility and recommendations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {geoSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${step.icon} text-xl text-teal-600`}></i>
                  </div>
                  <div className="text-3xl font-bold font-display text-teal-500/30">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-lg font-bold font-display text-navy-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-sm text-navy-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Visibility Tracking */}
      <section className="px-6 lg:px-12 py-20 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              AI Visibility Tracking
            </h2>
            <p className="text-xl text-navy-600 max-w-3xl mx-auto">
              We monitor how AI assistants respond to queries in your market across ChatGPT, Perplexity, Google SGE, Bing Copilot, and more—so you always know where you stand.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-chat-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Multi-Platform Testing
              </h3>
              <p className="text-navy-700 leading-relaxed mb-4">
                We test your brand visibility across all major AI platforms to give you complete visibility into your AI presence.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">ChatGPT, Perplexity, Google SGE</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Bing Copilot, Claude, Gemini</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Industry-specific queries</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Location-based searches</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Comparison requests</span>
                </li>
              </ul>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-search-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Competitive Analysis
              </h3>
              <p className="text-navy-700 leading-relaxed mb-4">
                See exactly which competitors AI recommends—and why—so you can outposition them.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Mention frequency tracking</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Sentiment analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Positioning insights</span>
                </li>
              </ul>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Progress Reporting
              </h3>
              <p className="text-navy-700 leading-relaxed mb-4">
                Monthly reports showing your AI visibility improvements and competitive positioning changes.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Visibility score trends</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Recommendation frequency</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Action recommendations</span>
                </li>
              </ul>
            </div>

            <div className="bg-navy-50 border border-navy-100 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-refresh-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold font-display text-navy-900 mb-3">
                Continuous Optimization
              </h3>
              <p className="text-navy-700 leading-relaxed mb-4">
                We don't just track—we continuously optimize based on what's working and what's not.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Content refinement</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Schema updates</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-teal-600 mt-0.5"></i>
                  <span className="text-sm text-navy-700">Authority building</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="px-6 lg:px-12 py-20 bg-navy-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-display text-navy-900 mb-6">
              What You Get
            </h2>
            <p className="text-xl text-navy-600">
              Complete GEO implementation and ongoing optimization
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-4">
              {deliverables.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-4 bg-teal-50 rounded-lg">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-sm text-navy-900"></i>
                  </div>
                  <span className="text-navy-900 font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-12 py-16 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-white mb-6">
            Ready to Dominate AI Search in Florida?
          </h2>
          <p className="text-xl text-navy-100 mb-8">
            Get your free AI visibility Assessment and see exactly where you stand today.
          </p>
          <Link
            to="/audit"
            className="btn-primary inline-block px-10 py-4 text-lg whitespace-nowrap cursor-pointer"
          >
            Get Free AI Visibility Assessment
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
